import { useMemo } from 'react';

import { Container, Tabs, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconHeart,
  IconListCheck,
  IconLock,
  IconUser,
} from '@tabler/icons-react';

import { useTranslation } from 'react-i18next';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useAuth from '@hooks/useAuth';

import Account from './account/Account';
import Bookings from './BookingsList';
import EditPassword from './EditPassword';
import WishList from './WishList';

export default function Home() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { user } = useAuth();
  const { t } = useTranslation();

  const [params, setParams] = useQueryParams();
  const page = useMemo(() => params.get('p'), [params]);

  const isMobile = useMediaQuery('(max-width: 36em)');

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleTabChange = (newTab: string) => {
    params.set('p', newTab);
    if (newTab !== 'bookings' && params.get('id')) {
      params.delete('id');
    }
    setParams(params);
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container size="85rem" py="xl" px="0" mih="100dvh">
      <Title mb="xl">
        {t('home.welcome')} {user && `${user.firstName} ${user.lastName}`}
      </Title>
      <Tabs
        orientation={isMobile ? 'horizontal' : 'vertical'}
        value={page || 'whishlist'}
        onChange={(value) => handleTabChange(value as string)}
        styles={{
          list: isMobile
            ? {
                overflowX: 'auto',
                flexWrap: 'nowrap',
              }
            : {},
          tabLabel: {
            textAlign: 'left',
          },
        }}
      >
        <Tabs.List grow>
          <Tabs.Tab value="whishlist" leftSection={<IconHeart />}>
            {t('home.tabs.wishlist')}
          </Tabs.Tab>
          <Tabs.Tab
            value="bookings"
            key="bookings"
            leftSection={<IconListCheck />}
          >
            {t('home.tabs.bookings')}
          </Tabs.Tab>
          <Tabs.Tab value="account" key="account" leftSection={<IconUser />}>
            {t('home.tabs.account')}
          </Tabs.Tab>
          <Tabs.Tab
            value="edit-password"
            key="edit-password"
            leftSection={<IconLock />}
          >
            {t('home.tabs.editPassword')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="whishlist" key="whishlist">
          <WishList />
        </Tabs.Panel>

        <Tabs.Panel value="bookings">
          <Bookings />
        </Tabs.Panel>

        <Tabs.Panel value="account">
          <Account />
        </Tabs.Panel>

        <Tabs.Panel value="edit-password">
          <EditPassword />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
